import React, { FC, useEffect } from 'react';
import './LogInForm.css';
import {
	ActionStatusHelper,
	Toast,
	FeedBackType,
	IoLockClosed,
	IoPerson,
	useAppNavigation,
	Form,
	Formik,
	InputField,
	PasswordField,
	FaUserLock,
	FormikProps,
	useToast,
	SubmitButton
} from '@/Shared';

import { useAuth } from '@/Domains';
import { logInInitValues, loginValidationSchema, LoginValues } from '../../Logic/LoginFormValidation';


interface LogInFormProps { }


/**
 * =====================================================
 * Component: LogInForm
 * =====================================================
 *
 * Description:
 * Provides a login form for users to authenticate into the application.
 * Integrates Formik for form handling and Yup for validation.
 * Uses `useAuth` hook for login logic and session management.
 * Uses `useToast` for displaying toast notifications.
 *
 * Responsibilities:
 * - Render login form with identifier and password fields
 * - Show validation errors via Formik and toast messages via `useToast`
 * - Submit form to `handleLogIn` and react to login response
 * - Redirect to home page if login succeeds
 *
 * Notes:
 * - Toast messages automatically display errors on failed login
 * - Submit button is disabled until form is dirty and valid
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
const LogInForm: FC<LogInFormProps> = () => {

	/** =====================================================
	* Hooks
	* =====================================================
	*
	* - toastHook: manages toast state and actions
	* - goToHome: navigation to home page
	* - useAuth: handles login logic and session state
	*/
	const toastHook = useToast();
	const { goToHome } = useAppNavigation();
	const { handleLogIn, logInResponse, userSession } = useAuth();

	const { isLoading, isSucceeded, isFailed, isIdle } = ActionStatusHelper.is(logInResponse.status)


	/** =====================================================
	 * Effect: React to Login Response
	 * =====================================================
	 *
	 * Redirects to home if login succeeds.
	 * Shows toast notification if login fails.
	 */
	useEffect(() => {
		if (isIdle && userSession) {
			goToHome();
		}
		if (isSucceeded) {
			goToHome();
		}
		else if (isFailed) {
			toastHook.openToast({
				data: {
					message: logInResponse.error || "Une erreur s'est produite",
					type: FeedBackType.ERROR
				}
			});
		}
	}, [isIdle, isSucceeded, isFailed, userSession]);

	return (
		<div className="LogInForm" data-testid="LogInForm">
			<div className='form  elevation-1"' >
				<Toast data={toastHook.toastData} onClose={toastHook.closeToast} />
				<div className="LogInForm__header form-header"><span className='icon'><IoLockClosed /></span> Connexion</div>
				<Formik
					initialValues={logInInitValues}
					validationSchema={loginValidationSchema}
					onSubmit={handleLogIn}
				>
					{(formikProps: FormikProps<LoginValues>) => {
						const { dirty, isValid } = formikProps;
						return (
							<Form className="form-body">


								<InputField
									required
									type='text'
									icon={<IoPerson />}
									name='identifier'
									placeholder="Saisir Email ou pseudo"
									autoComplete='off' />

								<PasswordField
									required
									icon={<FaUserLock />}
									name='password'
									placeholder="Saisir mot de passe"
									autoComplete='off' />

								<SubmitButton
									disabled={!dirty || !isValid}
									isLoading={isLoading}
									label='Se connecter'
								/>

							</Form>);
					}}
				</Formik>
			</div>
		</div>
	);
}

export default LogInForm;
